import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { FieldTypes, Form } from '@sitecore-jss/sitecore-jss-react-forms';
import { SitecoreContextReactContext } from '@sitecore-jss/sitecore-jss-react';
import { JssFormData } from '@sitecore-jss/sitecore-jss-forms/types/JssFormData';
import { FormResult } from '@sitecore-jss/sitecore-jss-forms/types/FormResult';
import { EventEnum, FormEventEnum, IFormEventEnumMap } from '../../enums/event.enum';
import { isInternalUrl } from '../../utils/linkUtils';
import { windowService } from '../../utils/window.utils.js';
import { sitecoreApiHost, sitecoreApiKey, defaultLanguage } from '../../temp/config';
import FieldValidationError from './components/fieldValidationError';
import fieldWrapper from './components/fieldWrapper';
import fieldFactory from './fieldFactory';
import { TSitecoreJssFormProps } from './types/sitecoreJssForm.types';
import { googleDataLayerPush } from '../../utils/analytics';
import customFieldTypes from './types/field.types';
import CustomFormContextProvider from './components/customFormContextProvider';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { TDropdownAnalyticsSendData } from './components/productSelector/dropdownContext';
import { DropdownListViewModel, FormField, instanceOfButtonFormField, SitecoreForm, ValueFormField, ViewModel } from '@sitecore-jss/sitecore-jss-forms';
import ThankYou from './components/thankYou';
import { APIPrefix } from '../../constants/api.constants';
import { TSelectedAddressContextValue } from './types/addressField.types';
import { localStorageService } from '../../utils/window.utils.js';
import { FORM_FIELDS_SAVED_VALUES } from '../../constants/formFields.constants';
import { getDropdownFieldValueText } from './hooks/getDropdownFieldValueText';
import { dropdownFieldItemIdAnalyticsTitleMap } from './constants/analyticsTtitleMap.constants';

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return (
      <Component
        {...props}
        router={{ location, navigate, params }}
      />
    );
  }
  return ComponentWithRouterProp;
}

const EmptyErrorComponent = (): JSX.Element => <></>;

const initialSelectedAddress = {
  city: '',
  zipcode: '',
  country: { name: '', isoCode: '' },
  state: { name: '', isoCode: '' },
};


const SitecoreJssForm: FC<TSitecoreJssFormProps> = (props) => {
  const { fields, router, params, rendering } = props;
  const thankyouMessageId = params?.thankyouMessageSetting || '';
  const [showThankyouMessage, updateShowThankyouMessage] = useState<boolean>(false);
  const [thankYouMessageFields, updateThankYouMessageFields] = useState<any>(undefined);
  const sitecoreContextFactory = useContext(SitecoreContextReactContext);
  const sitecoreContext = sitecoreContextFactory.context || {};
  const pageLanguage = sitecoreContext.language;
  const [productSelectorItems, setProductSelectorItems] = useState<TDropdownAnalyticsSendData[]>([]);
  const currentPageFieldsRef = useRef<SitecoreForm>(fields);
  const formRef = useRef<Form | null>(null);
  const initialAnalyticsDataRef = {
    PurchaseIntention: undefined,
    ContactReason: undefined,
    hasProductSelectorField: false,
    dropdown_fields: {
      other_dropdowns: {}
    }
  };
  const analyticsDataRef = useRef(initialAnalyticsDataRef);
  const selectedDealerName = useRef<string | undefined>(undefined);
  const [selectedAddress, setSelectedAddress] =
    useState<TSelectedAddressContextValue>(initialSelectedAddress);

  const storeAnalyticsData = (formData: JssFormData) => {
    const currentPageFieldDetailsMapByItemId: { [fieldTypeItemId: string]: FormField<ViewModel> } = {}
    const currentPageDropdownFieldDetails: ValueFormField<DropdownListViewModel>[] = [];
    const formDataMapByValueIdFieldName: { [fieldDetailsValueFieldName: string]: { key: string; value: string | File } } = {}

    currentPageFieldsRef?.current?.fields.forEach((eachFieldDetails) => {
      const { model: { fieldTypeItemId } } = eachFieldDetails;
      // Storing the first occurance of each field
      !currentPageFieldDetailsMapByItemId[fieldTypeItemId] && (currentPageFieldDetailsMapByItemId[fieldTypeItemId] = eachFieldDetails);
      // Gathering all normal dropdown fields
      if (fieldTypeItemId === FieldTypes.DropdownList) currentPageDropdownFieldDetails.push(eachFieldDetails as ValueFormField<DropdownListViewModel>);
    });

    formData.get().forEach((eachFormDataKeyValuePair) => (formDataMapByValueIdFieldName[eachFormDataKeyValuePair.key] = eachFormDataKeyValuePair));

    // Preserving the dropdown values listed in dropdownFieldItemIdAnalyticsTitleMap
    for (const eachFieldTypeItemId in dropdownFieldItemIdAnalyticsTitleMap) {
      const fieldDetails = currentPageFieldDetailsMapByItemId[eachFieldTypeItemId] as ValueFormField<DropdownListViewModel>;
      if (!fieldDetails) continue;
      if (eachFieldTypeItemId === customFieldTypes.purchaseIntentionFieldTypeItemId || eachFieldTypeItemId === customFieldTypes.contactReasonFieldTypeItemId) {
        analyticsDataRef.current[dropdownFieldItemIdAnalyticsTitleMap[eachFieldTypeItemId]] = getDropdownFieldValueText(fieldDetails, formDataMapByValueIdFieldName);
      } else {
        analyticsDataRef.current.dropdown_fields[dropdownFieldItemIdAnalyticsTitleMap[eachFieldTypeItemId]] = getDropdownFieldValueText(fieldDetails, formDataMapByValueIdFieldName);
      }
    }

    // Preserving the selected values of remaining normal dropdowns
    currentPageDropdownFieldDetails.forEach((eachDropdDownField) => {
      analyticsDataRef.current.dropdown_fields.other_dropdowns[eachDropdDownField.model.title] = getDropdownFieldValueText(eachDropdDownField, formDataMapByValueIdFieldName);
    })

    if (!analyticsDataRef.current.hasProductSelectorField) {
      const productSelectorField = currentPageFieldDetailsMapByItemId[customFieldTypes.productSelectorFieldTypeItemId];
      productSelectorField && (analyticsDataRef.current.hasProductSelectorField = true);
    }
  };

  const pushDataToAnalytics = () => {
    const formNameValue = fields?.metadata?.name;
    const productItems = () => {
      if (analyticsDataRef.current.hasProductSelectorField) {
        return productSelectorItems.map((obj) => ({
          item_id: obj?.modelsId,
          item_name: obj?.modelsTitle,
          product_line: obj?.lineTitle,
          product_series: obj?.seriesTitle,
          product_model: obj?.modelsTitle,
        }));
      }
    };

    googleDataLayerPush({
      event: 'form_submit',
      form_name: formNameValue,
      PurchaseIntention: analyticsDataRef.current.PurchaseIntention,
      ContactReason: analyticsDataRef.current.ContactReason,
      dropdown_fields: analyticsDataRef.current.dropdown_fields,
      items: productItems(),
      dealer_name: selectedDealerName.current,
    });
  };

  const resetSavedFormValues = () => {
    // restting to initial values
    analyticsDataRef.current = initialAnalyticsDataRef;
    selectedDealerName.current = undefined;
    setProductSelectorItems([]);
  };

  const successResponseHandler = (res, formData) => {
    storeAnalyticsData(formData);
    const isSubmitButtonClicked = currentPageFieldsRef.current.fields.find(
      (field) =>
        instanceOfButtonFormField(field) &&
        field?.buttonField?.name === formRef.current?.state.submitButton &&
        field?.navigationStepField.value === '0'
    );
    if (res?.success && isSubmitButtonClicked) {
      if (res?.errors.length === 0) {
        pushDataToAnalytics();
        setSelectedAddress(initialSelectedAddress);
      }
      updateShowThankyouMessage(true);
      resetSavedFormValues();
      const formSubmitEvent: CustomEvent<IFormEventEnumMap[FormEventEnum.FORM_SUBMITTED]> = new CustomEvent(FormEventEnum.FORM_SUBMITTED, {
        detail: { formDatasource: rendering?.dataSource || '' },
      });
      windowService().dispatchEvent?.(formSubmitEvent);
    }
    if (res.nextForm) currentPageFieldsRef.current = res.nextForm; // Might be causing the above mentioned issue in single page form
    return res;
  };

  const fetchData = async () => {
    const getApiURL = `/${APIPrefix}/thankyoumessage/message/${thankyouMessageId}/${pageLanguage}`;
    try {
      const res = await fetch(getApiURL);
      const result = await res.json();
      updateThankYouMessageFields({ ...result });
    } catch (error) {
      console.error(`${error} occurred while fetching data`);
    }
  };

  useEffect(() => {
    thankyouMessageId && fetchData();
    return () => {
      localStorageService()?.removeItem(FORM_FIELDS_SAVED_VALUES);
    }
  }, []);

  return (
    <>
      {showThankyouMessage && thankYouMessageFields ?
        (<ThankYou
          redirectionLink={thankYouMessageFields?.redirectPageLink}
          title={thankYouMessageFields?.thankyouHeading}
          message={thankYouMessageFields?.thankyouMessage}
        />) : (
          <section className="sitecore-jss-form">
            <CustomFormContextProvider
              sitecoreForm={fields}
              params={params}
              _selectedAddress={[selectedAddress, setSelectedAddress]}
              setProductSelector={(value) => setProductSelectorItems(value)}
              setSelectedDealerName={(dealerName) => (selectedDealerName.current = dealerName || '')}
            >
              <Form
                form={fields}
                ref={formRef}
                language={pageLanguage || defaultLanguage}
                sitecoreApiHost={sitecoreApiHost}
                sitecoreApiKey={sitecoreApiKey}
                fieldFactory={fieldFactory}
                errorComponent={EmptyErrorComponent}
                fieldValidationErrorsComponent={FieldValidationError}
                fieldWrapperComponent={fieldWrapper}
                formFetcher={(formData: JssFormData, endpoint: string) => {
                  windowService().dispatchEvent?.(new CustomEvent(EventEnum.ADD_LOADER_QUEUE_ITEM));
                  return fetch(endpoint, {
                    body: formData.toMultipartFormData(),
                    method: 'post',
                    credentials: 'include',
                  })
                    .then((res) => res.json())
                    .then((res) => successResponseHandler(res as FormResult, formData))
                    .finally(() => windowService().dispatchEvent?.(new CustomEvent(EventEnum.REMOVE_LOADER_QUEUE_ITEM)));
                }}
                onRedirect={(url) => {
                  if (isInternalUrl(url)) {
                    router.navigate(url);
                  } else {
                    windowService().location.href = url;
                  }
                }}
              />
            </CustomFormContextProvider>
          </section>
        )
      }
    </>
  );
};

export default withRouter(SitecoreJssForm);
